import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import {
  getSimpleFilterJson,
  parseCompoundGroups,
  serializeCompoundGroups,
} from "./compound-filter-utils";
import {
  ComparisonFilterValue,
  ComparisonOperator,
  CompoundCondition,
  CompoundFilterGroup,
  FilterMenuItem,
  TextOperator,
} from "./menu/types";

export type FilterJson = Record<string, unknown>;

type SimpleFilterJson = Record<string, Record<string, unknown>>;

export interface TextFilterEntry {
  fieldName: string;
  label: string | JSX.Element;
  operator: TextOperator;
  values: string[];
  options?: { label: string | JSX.Element; value: string }[];
}

export interface ComparisonFilterEntry {
  fieldName: string;
  label: string | JSX.Element;
  operator: ComparisonOperator;
  value: string;
  type: "DATE" | "NUMERIC";
}

export interface BooleanFilterEntry {
  fieldName: string;
  label: string | JSX.Element;
  value: string;
  type: "BOOLEAN";
}

export interface CompoundFilterEntry {
  type: "COMPOUND";
  group: CompoundFilterGroup;
}

export type FilterEntry =
  | TextFilterEntry
  | ComparisonFilterEntry
  | BooleanFilterEntry
  | CompoundFilterEntry;

export interface TextFilterSelection {
  operator: TextOperator;
  values: string[];
}

export function parseFilterJson(raw: string | null): FilterJson {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return {};
    }
    return parsed;
  } catch {
    return {};
  }
}

const LIST_TEXT_OPERATORS = ["in", "not_in"] as const;
const SCALAR_TEXT_OPERATORS = ["neq", "eq"] as const;

function stringsOnly(values: unknown[]): string[] {
  return values.filter((value): value is string => typeof value === "string");
}

function selectionOf(
  operator: TextOperator,
  values: string[],
): TextFilterSelection | null {
  return values.length > 0 ? { operator, values } : null;
}

export function parseTextFilterSelection(
  fieldFilter: unknown,
): TextFilterSelection | null {
  if (!fieldFilter || typeof fieldFilter !== "object") return null;
  const operators = fieldFilter as Record<string, unknown>;

  for (const operator of LIST_TEXT_OPERATORS) {
    const values = operators[operator];
    if (Array.isArray(values)) {
      return selectionOf(operator, stringsOnly(values));
    }
  }
  for (const operator of SCALAR_TEXT_OPERATORS) {
    if (operator in operators) {
      return selectionOf(operator, stringsOnly([operators[operator]]));
    }
  }
  return null;
}

function deriveSelectedValues(
  filterJson: SimpleFilterJson,
  menuItems: FilterMenuItem[],
): Record<string, TextFilterSelection> {
  const result: Record<string, TextFilterSelection> = {};
  for (const item of menuItems) {
    if (item.type !== "TEXT") continue;
    const selection = parseTextFilterSelection(filterJson[item.name]);
    if (selection) result[item.name] = selection;
  }
  return result;
}

function deriveComparisonValues(
  filterJson: SimpleFilterJson,
  menuItems: FilterMenuItem[],
): Record<string, ComparisonFilterValue> {
  const result: Record<string, ComparisonFilterValue> = {};
  for (const item of menuItems) {
    if (item.type !== "DATE" && item.type !== "NUMERIC") continue;
    const fieldFilter = filterJson[item.name];
    if (!fieldFilter) continue;
    const [operator, value] = Object.entries(fieldFilter)[0] ?? [];
    if (operator && value !== undefined) {
      result[item.name] = {
        operator: operator as ComparisonOperator,
        value: String(value),
      };
    }
  }
  return result;
}

function deriveBooleanValues(
  filterJson: SimpleFilterJson,
  menuItems: FilterMenuItem[],
): Record<string, string> {
  const result: Record<string, string> = {};
  for (const item of menuItems) {
    if (item.type !== "BOOLEAN") continue;
    const fieldFilter = filterJson[item.name];
    if (!fieldFilter) continue;
    if ("eq" in fieldFilter) {
      result[item.name] = String(fieldFilter.eq);
    }
  }
  return result;
}

function buildFilterJson(
  selectedValues: Record<string, { operator: TextOperator; values: string[] }>,
  comparisonValues: Record<string, ComparisonFilterValue>,
  booleanValues: Record<string, string>,
  compoundGroups: CompoundFilterGroup[],
): FilterJson {
  const filter: FilterJson = {};

  for (const [field, { operator, values }] of Object.entries(selectedValues)) {
    if (values.length === 0) continue;
    if (operator === "in") {
      filter[field] = values.length === 1 ? { eq: values[0] } : { in: values };
    } else if (operator === "not_in") {
      filter[field] =
        values.length === 1 ? { neq: values[0] } : { not_in: values };
    } else if (operator === "neq") {
      filter[field] = { neq: values[0] };
    } else {
      filter[field] = { eq: values[0] };
    }
  }

  for (const [field, { operator, value }] of Object.entries(comparisonValues)) {
    if (!value) continue;
    filter[field] = { [operator]: value };
  }

  for (const [field, value] of Object.entries(booleanValues)) {
    filter[field] = { eq: value };
  }

  const compoundJson = serializeCompoundGroups(compoundGroups);
  Object.assign(filter, compoundJson);

  return filter;
}

const MAX_CONDITIONS_PER_GROUP = 2;

export function useFilterState(filterMenuItemGroups: FilterMenuItem[][]) {
  const [searchParams, setSearchParams] = useSearchParams();

  const allMenuItems = useMemo(
    () => filterMenuItemGroups.flat(),
    [filterMenuItemGroups],
  );

  const filterJson = useMemo(
    () => parseFilterJson(searchParams.get("filter")),
    [searchParams],
  );

  const compoundGroups = useMemo(
    () => parseCompoundGroups(filterJson, allMenuItems),
    [filterJson, allMenuItems],
  );

  const simpleFilterJson = useMemo(
    () => getSimpleFilterJson(filterJson),
    [filterJson],
  );

  const selectedValues = useMemo(
    () => deriveSelectedValues(simpleFilterJson, allMenuItems),
    [simpleFilterJson, allMenuItems],
  );

  const comparisonValues = useMemo(
    () => deriveComparisonValues(simpleFilterJson, allMenuItems),
    [simpleFilterJson, allMenuItems],
  );

  const booleanValues = useMemo(
    () => deriveBooleanValues(simpleFilterJson, allMenuItems),
    [simpleFilterJson, allMenuItems],
  );

  const hasActiveFilters = useMemo(
    () =>
      Object.keys(selectedValues).length > 0 ||
      Object.keys(comparisonValues).length > 0 ||
      Object.keys(booleanValues).length > 0 ||
      compoundGroups.length > 0,
    [selectedValues, comparisonValues, booleanValues, compoundGroups],
  );

  const activeFilterEntries: FilterEntry[] = useMemo(() => {
    const entries: FilterEntry[] = [];

    for (const item of allMenuItems) {
      if (item.type === "TEXT") {
        const sel = selectedValues[item.name];
        if (!sel || sel.values.length === 0) continue;
        entries.push({
          fieldName: item.name,
          label: item.label,
          operator: sel.operator,
          values: sel.values,
          options: item.options,
        });
      } else if (item.type === "BOOLEAN") {
        const val = booleanValues[item.name];
        if (val === undefined) continue;
        entries.push({
          fieldName: item.name,
          label: item.label,
          value: val,
          type: "BOOLEAN",
        });
      } else {
        const comp = comparisonValues[item.name];
        if (!comp) continue;
        entries.push({
          fieldName: item.name,
          label: item.label,
          operator: comp.operator,
          value: comp.value,
          type: item.type,
        });
      }
    }

    for (const group of compoundGroups) {
      entries.push({ type: "COMPOUND", group });
    }

    return entries;
  }, [
    allMenuItems,
    selectedValues,
    comparisonValues,
    booleanValues,
    compoundGroups,
  ]);

  const updateUrl = useCallback(
    (
      nextSelected: Record<
        string,
        { operator: TextOperator; values: string[] }
      >,
      nextComparison: Record<string, ComparisonFilterValue>,
      nextBoolean: Record<string, string>,
      nextCompound: CompoundFilterGroup[],
    ) => {
      const newFilter = buildFilterJson(
        nextSelected,
        nextComparison,
        nextBoolean,
        nextCompound,
      );
      setSearchParams(
        (prev) => {
          if (Object.keys(newFilter).length === 0) {
            prev.delete("filter");
          } else {
            prev.set("filter", JSON.stringify(newFilter));
          }
          return prev;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const handleSelectionChange = useCallback(
    (name: string, values: string[]) => {
      const existing = selectedValues[name];
      let operator = existing?.operator ?? "in";

      // Auto-upgrade/downgrade operator based on value count
      if (values.length > 1) {
        if (operator === "eq") operator = "in";
        else if (operator === "neq") operator = "not_in";
      } else if (values.length === 1) {
        if (operator === "in") operator = "eq";
        else if (operator === "not_in") operator = "neq";
      }

      const next = { ...selectedValues, [name]: { operator, values } };
      updateUrl(next, comparisonValues, booleanValues, compoundGroups);
    },
    [
      selectedValues,
      comparisonValues,
      booleanValues,
      compoundGroups,
      updateUrl,
    ],
  );

  const handleTextOperatorChange = useCallback(
    (name: string, operator: TextOperator) => {
      const existing = selectedValues[name];
      if (!existing) return;

      // Ensure operator matches value count
      let finalOperator = operator;
      if (existing.values.length > 1) {
        if (finalOperator === "eq") finalOperator = "in";
        else if (finalOperator === "neq") finalOperator = "not_in";
      } else if (existing.values.length === 1) {
        if (finalOperator === "in") finalOperator = "eq";
        else if (finalOperator === "not_in") finalOperator = "neq";
      }

      const next = {
        ...selectedValues,
        [name]: { operator: finalOperator, values: existing.values },
      };
      updateUrl(next, comparisonValues, booleanValues, compoundGroups);
    },
    [
      selectedValues,
      comparisonValues,
      booleanValues,
      compoundGroups,
      updateUrl,
    ],
  );

  const handleComparisonChange = useCallback(
    (name: string, value: ComparisonFilterValue) => {
      const next = { ...comparisonValues, [name]: value };
      updateUrl(selectedValues, next, booleanValues, compoundGroups);
    },
    [
      selectedValues,
      comparisonValues,
      booleanValues,
      compoundGroups,
      updateUrl,
    ],
  );

  const handleBooleanChange = useCallback(
    (name: string, value: string) => {
      const next = { ...booleanValues, [name]: value };
      updateUrl(selectedValues, comparisonValues, next, compoundGroups);
    },
    [
      selectedValues,
      comparisonValues,
      booleanValues,
      compoundGroups,
      updateUrl,
    ],
  );

  const removeFilter = useCallback(
    (fieldName: string) => {
      const nextSelected = { ...selectedValues };
      delete nextSelected[fieldName];
      const nextComparison = { ...comparisonValues };
      delete nextComparison[fieldName];
      const nextBoolean = { ...booleanValues };
      delete nextBoolean[fieldName];
      updateUrl(nextSelected, nextComparison, nextBoolean, compoundGroups);
    },
    [
      selectedValues,
      comparisonValues,
      booleanValues,
      compoundGroups,
      updateUrl,
    ],
  );

  const addCompoundGroup = useCallback(
    (group: CompoundFilterGroup) => {
      const capped = {
        ...group,
        conditions: group.conditions.slice(0, MAX_CONDITIONS_PER_GROUP),
      };
      const next = [...compoundGroups, capped];
      updateUrl(selectedValues, comparisonValues, booleanValues, next);
    },
    [
      selectedValues,
      comparisonValues,
      booleanValues,
      compoundGroups,
      updateUrl,
    ],
  );

  const updateCompoundCondition = useCallback(
    (
      groupId: string,
      conditionIndex: number,
      update: Partial<CompoundCondition>,
    ) => {
      const next = compoundGroups.map((g) => {
        if (g.id !== groupId) return g;
        const conditions = g.conditions.map((c, i) => {
          if (i !== conditionIndex) return c;
          return { ...c, ...update } as CompoundCondition;
        });
        return { ...g, conditions };
      });
      updateUrl(selectedValues, comparisonValues, booleanValues, next);
    },
    [
      selectedValues,
      comparisonValues,
      booleanValues,
      compoundGroups,
      updateUrl,
    ],
  );

  const removeCompoundCondition = useCallback(
    (groupId: string, conditionIndex: number) => {
      const next: CompoundFilterGroup[] = [];
      for (const g of compoundGroups) {
        if (g.id !== groupId) {
          next.push(g);
          continue;
        }
        const conditions = g.conditions.filter((_, i) => i !== conditionIndex);
        if (conditions.length > 0) {
          next.push({ ...g, conditions });
        }
      }
      updateUrl(selectedValues, comparisonValues, booleanValues, next);
    },
    [
      selectedValues,
      comparisonValues,
      booleanValues,
      compoundGroups,
      updateUrl,
    ],
  );

  const removeCompoundGroup = useCallback(
    (groupId: string) => {
      const next = compoundGroups.filter((g) => g.id !== groupId);
      updateUrl(selectedValues, comparisonValues, booleanValues, next);
    },
    [
      selectedValues,
      comparisonValues,
      booleanValues,
      compoundGroups,
      updateUrl,
    ],
  );

  const addConditionToGroup = useCallback(
    (groupId: string, condition: CompoundCondition) => {
      const next = compoundGroups.map((g) => {
        if (g.id !== groupId) return g;
        if (g.conditions.length >= MAX_CONDITIONS_PER_GROUP) return g;
        return { ...g, conditions: [...g.conditions, condition] };
      });
      updateUrl(selectedValues, comparisonValues, booleanValues, next);
    },
    [
      selectedValues,
      comparisonValues,
      booleanValues,
      compoundGroups,
      updateUrl,
    ],
  );

  const setCompoundCondition = useCallback(
    (groupId: string, index: number, condition: CompoundCondition) => {
      const next = compoundGroups.map((g) => {
        if (g.id !== groupId) return g;
        const conditions = [...g.conditions];
        conditions[index] = condition;
        return { ...g, conditions: conditions.filter(Boolean) };
      });
      updateUrl(selectedValues, comparisonValues, booleanValues, next);
    },
    [
      selectedValues,
      comparisonValues,
      booleanValues,
      compoundGroups,
      updateUrl,
    ],
  );

  const toggleCompoundLogic = useCallback(
    (groupId: string) => {
      const group = compoundGroups.find((g) => g.id === groupId);
      if (!group) return;

      const targetLogic = group.logic === "and" ? "or" : "and";
      if (compoundGroups.some((g) => g.logic === targetLogic)) return;

      const next = compoundGroups.map((g) => {
        if (g.id !== groupId) return g;
        return {
          ...g,
          id: `compound-${targetLogic}` as const,
          logic: targetLogic as "and" | "or",
        };
      });
      updateUrl(selectedValues, comparisonValues, booleanValues, next);
    },
    [
      selectedValues,
      comparisonValues,
      booleanValues,
      compoundGroups,
      updateUrl,
    ],
  );

  const clearAllFilters = useCallback(() => {
    updateUrl({}, {}, {}, []);
  }, [updateUrl]);

  // For backward compat with the menu component (which uses string[] not {operator, values})
  const selectedValuesSimple = useMemo(() => {
    const result: Record<string, string[]> = {};
    for (const [key, val] of Object.entries(selectedValues)) {
      result[key] = val.values;
    }
    return result;
  }, [selectedValues]);

  return {
    selectedValues: selectedValuesSimple,
    comparisonValues,
    booleanValues,
    compoundGroups,
    hasActiveFilters,
    activeFilterEntries,
    handleSelectionChange,
    handleComparisonChange,
    handleBooleanChange,
    handleTextOperatorChange,
    removeFilter,
    addCompoundGroup,
    addConditionToGroup,
    setCompoundCondition,
    updateCompoundCondition,
    removeCompoundCondition,
    removeCompoundGroup,
    toggleCompoundLogic,
    clearAllFilters,
  };
}
